import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { File, Loader2, MoreHorizontal, RotateCcw, RotateCw } from 'lucide-react';
import { toast } from 'sonner';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  ATTACHMENT_FILE_LIST_SCROLL_THRESHOLD,
  attachmentDownloadPath,
  attachmentIsSticker,
  attachmentMediaPreviewVariant,
  fetchAuthorizedMediaBlob,
  filterRenderableStoredAttachments,
  stickerPresentationMode,
} from '@/lib/realtime/message-attachments';
import { copyImageBlobToClipboard, copyImageFromUrl } from '@/lib/clipboard/image-clipboard';
import { StickerSlot } from '@/components/chat/sticker-slot';
import { cn } from '@/lib/utils';

const fileListOuterClass = 'w-full rounded-xl border border-border/60 bg-muted/15';

const MessageBubbleAttachmentActionsContext = createContext(null);

/**
 * Wrap a single message bubble row so attachment "…" menus show on row hover and hide 2s after pointer leaves
 * (unless an attachment menu is open — then hide is deferred until the menu closes).
 */
export function MessageBubbleAttachmentActionsProvider({ className, children, ...rest }) {
  const [showActions, setShowActions] = useState(false);
  const hideTimerRef = useRef(null);
  const pointerInsideRef = useRef(false);
  const menuOpenRef = useRef(false);

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const scheduleHide = useCallback(() => {
    clearHideTimer();
    hideTimerRef.current = setTimeout(() => {
      if (!menuOpenRef.current) {
        setShowActions(false);
      }
      hideTimerRef.current = null;
    }, 1000);
  }, [clearHideTimer]);

  const onAttachmentMenuOpenChange = useCallback(
    (open) => {
      menuOpenRef.current = open;
      if (open) {
        clearHideTimer();
        setShowActions(true);
      } else if (!pointerInsideRef.current) {
        scheduleHide();
      }
    },
    [clearHideTimer, scheduleHide],
  );

  const handleMouseEnter = useCallback(() => {
    pointerInsideRef.current = true;
    clearHideTimer();
    setShowActions(true);
  }, [clearHideTimer]);

  const handleMouseLeave = useCallback(() => {
    pointerInsideRef.current = false;
    if (!menuOpenRef.current) {
      scheduleHide();
    }
  }, [scheduleHide]);

  useEffect(() => () => clearHideTimer(), [clearHideTimer]);

  const value = useMemo(
    () => ({ showActions, onAttachmentMenuOpenChange }),
    [showActions, onAttachmentMenuOpenChange],
  );

  return (
    <MessageBubbleAttachmentActionsContext.Provider value={value}>
      <div className={className} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} {...rest}>
        {children}
      </div>
    </MessageBubbleAttachmentActionsContext.Provider>
  );
}

function useMessageBubbleAttachmentActions() {
  return useContext(MessageBubbleAttachmentActionsContext);
}

function attachmentDisplayName(att) {
  if (att.filename != null) {
    return String(att.filename);
  }
  if (att.name != null) {
    return String(att.name);
  }
  return 'download';
}

/**
 * "..." menu that fetches the attachment by path and saves it (stickers and any path-only case).
 * @param {{ path: string; fileName: string; canCopyImage?: boolean }} props
 */
function AttachmentPathDownloadMenu({ path, fileName, canCopyImage = false }) {
  const [loading, setLoading] = useState(false);
  const [copying, setCopying] = useState(false);
  const actionsCtx = useMessageBubbleAttachmentActions();
  const showTriggers = actionsCtx?.showActions ?? true;

  const handleDownload = async () => {
    setLoading(true);
    try {
      const blob = await fetchAuthorizedMediaBlob(path);
      const u = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = u;
      a.download = fileName || 'download';
      a.rel = 'noopener';
      a.click();
      URL.revokeObjectURL(u);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  const handleCopyImage = async () => {
    setCopying(true);
    try {
      const blob = await fetchAuthorizedMediaBlob(path);
      await copyImageBlobToClipboard(blob);
      toast.success('Image copied');
    } catch {
      toast.error("Couldn't copy image");
    } finally {
      setCopying(false);
    }
  };

  return (
    <Popover onOpenChange={actionsCtx?.onAttachmentMenuOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="secondary"
          size="icon"
          data-capture-ignore="true"
          className={cn(
            'absolute -right-1 -top-1 z-10 h-7 w-8 shrink-0 rounded-md border border-border/60 bg-background/90 shadow-sm backdrop-blur-sm transition-opacity duration-200 hover:bg-background',
            !showTriggers && 'pointer-events-none opacity-0',
          )}
          aria-label="Attachment actions"
        >
          <MoreHorizontal className="h-4 w-4" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-1" align="end">
        {canCopyImage ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 w-full justify-start px-3 text-xs font-medium"
            disabled={copying}
            onClick={() => void handleCopyImage()}
          >
            {copying ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
            Copy image
          </Button>
        ) : null}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-full justify-start px-3 text-xs font-medium"
          disabled={loading}
          onClick={() => void handleDownload()}
        >
          {loading ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
          Download
        </Button>
      </PopoverContent>
    </Popover>
  );
}

/**
 * @param {{ blobUrl: string; fileName: string; layout?: 'overlay' | 'inline'; canCopyImage?: boolean }} props
 */
function AttachmentBlobDownloadMenu({ blobUrl, fileName, layout = 'overlay', canCopyImage = false }) {
  const [copying, setCopying] = useState(false);
  const actionsCtx = useMessageBubbleAttachmentActions();
  const showTriggers = actionsCtx?.showActions ?? true;
  const triggerClass =
    layout === 'overlay'
      ? 'absolute right-1.5 top-1.5 z-10 h-8 w-8 shrink-0 rounded-md border border-border/60 bg-background/90 shadow-sm backdrop-blur-sm transition-opacity duration-200 hover:bg-background'
      : 'h-8 w-8 shrink-0 rounded-md border border-border/60 bg-background/90 shadow-sm backdrop-blur-sm transition-opacity duration-200 hover:bg-background';

  const handleCopyImage = async () => {
    setCopying(true);
    try {
      await copyImageFromUrl(blobUrl);
      toast.success('Image copied');
    } catch {
      toast.error("Couldn't copy image");
    } finally {
      setCopying(false);
    }
  };

  return (
    <Popover onOpenChange={actionsCtx?.onAttachmentMenuOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="secondary"
          size="icon"
          data-capture-ignore="true"
          className={cn(triggerClass, !showTriggers && 'pointer-events-none opacity-0')}
          aria-label="Attachment actions"
        >
          <MoreHorizontal className="h-4 w-4" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-1" align="end">
        {canCopyImage ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 w-full justify-start px-3 text-xs font-medium"
            disabled={copying}
            onClick={() => void handleCopyImage()}
          >
            {copying ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
            Copy image
          </Button>
        ) : null}
        <Button variant="ghost" size="sm" className="h-8 w-full justify-start px-3 text-xs font-medium" asChild>
          <a href={blobUrl} download={fileName} target="_blank" rel="noopener noreferrer">
            Download
          </a>
        </Button>
      </PopoverContent>
    </Popover>
  );
}

/**
 * @param {{ path: string; variant: 'image' | 'video' | 'audio'; fileName: string; rowKey: string }} props
 */
function MediaPreviewSlot({ path, variant, fileName, rowKey }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxRotation, setLightboxRotation] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let objUrl = '';
    fetchAuthorizedMediaBlob(path)
      .then((blob) => {
        if (cancelled) {
          return;
        }
        objUrl = URL.createObjectURL(blob);
        setUrl(objUrl);
      })
      .catch(() => {
        if (!cancelled) {
          setFailed(true);
        }
      });
    return () => {
      cancelled = true;
      if (objUrl) {
        URL.revokeObjectURL(objUrl);
      }
    };
  }, [path, rowKey]);

  if (failed) {
    return <p className="text-xs text-muted-foreground">Attachment unavailable</p>;
  }
  if (!url) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-border/50 bg-muted/20 px-3 py-4 text-xs text-muted-foreground">
        <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
        <span>Loading...</span>
      </div>
    );
  }

  if (variant === 'image') {
    // Rotation keeps the layout box unrotated — when sideways, swap the fit
    // constraints so the rotated visual still fits the viewport.
    const lightboxSideways = ((lightboxRotation % 180) + 180) % 180 !== 0;
    return (
      <>
        <div className="relative inline-block max-w-full">
          <AttachmentBlobDownloadMenu blobUrl={url} fileName={fileName} canCopyImage />
          <img
            src={url}
            alt={fileName}
            onDoubleClick={() => {
              setLightboxRotation(0);
              setLightboxOpen(true);
            }}
            className="max-h-[min(280px,45vh)] max-w-full cursor-zoom-in rounded-xl border border-border/60 object-contain shadow-sm"
          />
        </div>
        <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
          <DialogContent
            aria-describedby={undefined}
            className={cn(
              'max-h-[min(92vh,92dvh)] w-auto max-w-[min(96vw,96dvw)] place-content-center place-items-center border-0 bg-transparent p-0 shadow-none',
              // Default close uses muted text + opacity — invisible on bright images; keep a solid control.
              '[&>button]:right-3 [&>button]:top-3 [&>button]:z-60 [&>button]:flex [&>button]:h-10 [&>button]:w-10 [&>button]:items-center [&>button]:justify-center',
              '[&>button]:rounded-full [&>button]:border-0 [&>button]:bg-black/70 [&>button]:text-white! [&>button]:opacity-100 [&>button]:shadow-md',
              '[&>button]:backdrop-blur-sm [&>button]:hover:bg-black/85 [&>button]:hover:text-white! [&>button]:hover:opacity-100',
              '[&>button]:focus:ring-2 [&>button]:focus:ring-white/50 [&>button]:focus:ring-offset-0 [&>button]:focus:ring-offset-transparent',
              '[&>button]:data-[state=open]:bg-black/70 [&>button]:data-[state=open]:text-white!',
            )}
          >
            <DialogTitle className="sr-only">{fileName}</DialogTitle>
            <img
              src={url}
              alt={fileName}
              style={{
                transform: `rotate(${lightboxRotation}deg)`,
                maxWidth: lightboxSideways ? 'min(88vh, 88dvh)' : 'min(94vw, 94dvw)',
                maxHeight: lightboxSideways ? 'min(94vw, 94dvw)' : 'min(88vh, 88dvh)',
              }}
              className="rounded-lg object-contain transition-transform duration-200 motion-reduce:transition-none"
            />
            <div className="absolute bottom-3 left-1/2 z-60 flex -translate-x-1/2 items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                size="icon"
                onClick={() => setLightboxRotation((deg) => deg - 90)}
                className="h-10 w-10 rounded-full border-0 bg-black/70 text-white shadow-md backdrop-blur-sm hover:bg-black/85 hover:text-white focus-visible:ring-2 focus-visible:ring-white/50"
                aria-label="Rotate left"
              >
                <RotateCcw className="h-5 w-5" aria-hidden />
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="icon"
                onClick={() => setLightboxRotation((deg) => deg + 90)}
                className="h-10 w-10 rounded-full border-0 bg-black/70 text-white shadow-md backdrop-blur-sm hover:bg-black/85 hover:text-white focus-visible:ring-2 focus-visible:ring-white/50"
                aria-label="Rotate right"
              >
                <RotateCw className="h-5 w-5" aria-hidden />
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }
  if (variant === 'video') {
    return (
      <div className="relative inline-block max-w-full">
        <AttachmentBlobDownloadMenu blobUrl={url} fileName={fileName} />
        <video
          src={url}
          controls
          className="max-h-[min(240px,40vh)] max-w-full rounded-xl border border-border/60 bg-black shadow-sm"
          playsInline
        />
      </div>
    );
  }
  return (
    <div className="mt-0.5 flex w-full max-w-md flex-col items-end gap-1.5">
      <AttachmentBlobDownloadMenu blobUrl={url} fileName={fileName} layout="inline" />
      <audio src={url} controls className="w-full rounded-lg" />
    </div>
  );
}

/**
 * @param {{ path: string; fileName: string; mimeHint: string; rowKey: string }} props
 */
function AttachmentDownloadRow({ path, fileName, mimeHint, rowKey }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [phase, setPhase] = useState('loading');
  const actionsCtx = useMessageBubbleAttachmentActions();
  const showTriggers = actionsCtx?.showActions ?? true;

  useEffect(() => {
    let cancelled = false;
    let objUrl = '';
    fetchAuthorizedMediaBlob(path)
      .then((blob) => {
        if (cancelled) {
          return;
        }
        objUrl = URL.createObjectURL(blob);
        setBlobUrl(objUrl);
        setPhase('ready');
      })
      .catch(() => {
        if (!cancelled) {
          setPhase('error');
        }
      });
    return () => {
      cancelled = true;
      if (objUrl) {
        URL.revokeObjectURL(objUrl);
      }
    };
  }, [path, rowKey]);

  const typeLabel = String(mimeHint || '').trim() || 'File';

  return (
    <div className="flex w-full min-w-[min(100%,12rem)] items-center gap-3 rounded-lg border border-border/50 bg-background/95 px-3 py-2.5 shadow-sm backdrop-blur-sm transition-colors hover:border-border">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <File className="h-5 w-5" strokeWidth={1.75} aria-hidden />
      </div>
      <div className="min-w-0 flex-1 overflow-hidden">
        <p className="truncate text-sm font-medium leading-tight text-foreground">{fileName}</p>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">{typeLabel}</p>
      </div>
      <div className="shrink-0">
        {phase === 'loading' && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-label="Loading" />}
        {phase === 'ready' && blobUrl && (
          <Popover onOpenChange={actionsCtx?.onAttachmentMenuOpenChange}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="secondary"
                size="icon"
                className={cn(
                  'h-8 w-8 rounded-md border border-border/60 transition-opacity duration-200',
                  !showTriggers && 'pointer-events-none opacity-0',
                )}
                aria-label="Attachment actions"
              >
                <MoreHorizontal className="h-4 w-4" aria-hidden />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-1" align="end">
              <Button variant="ghost" size="sm" className="h-8 w-full justify-start px-3 text-xs font-medium" asChild>
                <a href={blobUrl} download={fileName} target="_blank" rel="noopener noreferrer">
                  Download
                </a>
              </Button>
            </PopoverContent>
          </Popover>
        )}
        {phase === 'error' && <span className="text-xs text-muted-foreground">Unavailable</span>}
      </div>
    </div>
  );
}

/**
 * In-message UI: stickers (WebP / Lottie JSON / dotLottie / .was), then image / video / audio, then file list.
 * @param {{ attachments: unknown }} props
 */
export function ChatMessageAttachments({ attachments }) {
  const list = Array.isArray(attachments) ? attachments : [];
  const stored = filterRenderableStoredAttachments(list);

  if (!stored.length) {
    return null;
  }

  /** @type {Array<{ path: string; variant: 'image'|'video'|'audio'; fileName: string; rowKey: string }>} */
  const mediaItems = [];
  /** @type {Array<{ path: string; label: string; rowKey: string; presentationMode: ReturnType<typeof stickerPresentationMode> }>} */
  const stickerItems = [];
  /** @type {Array<{ path: string; fileName: string; mimeHint: string; rowKey: string }>} */
  const fileItems = [];

  stored.forEach((att, index) => {
    const path = attachmentDownloadPath(att);
    const fileName = attachmentDisplayName(att);
    const rowKey = `${path}-${index}-${fileName}`;
    const mimeHint = String(att.mimeType || att.contentType || '');
    if (attachmentIsSticker(att)) {
      stickerItems.push({
        path,
        label: fileName,
        rowKey,
        presentationMode: stickerPresentationMode(att),
      });
      return;
    }
    const variant = attachmentMediaPreviewVariant(att);
    if (variant) {
      mediaItems.push({ path, variant, fileName, rowKey });
    } else {
      fileItems.push({ path, fileName, mimeHint, rowKey });
    }
  });

  const fileListBody =
    fileItems.length > 0 ? (
      <div className="space-y-2 p-2.5 pr-2">
        <p className="px-0.5 text-[0.65rem] font-bold uppercase tracking-wider text-muted-foreground">
          {fileItems.length === 1 ? 'File' : 'Files'}
        </p>
        <div className="space-y-1.5">
          {fileItems.map((item) => (
            <AttachmentDownloadRow
              key={item.rowKey}
              path={item.path}
              fileName={item.fileName}
              mimeHint={item.mimeHint}
              rowKey={item.rowKey}
            />
          ))}
        </div>
      </div>
    ) : null;

  return (
    <div className="mt-2 flex min-w-[min(100%,18rem)] max-w-full flex-col gap-3">
      {stickerItems.length > 0 && (
        <div className="flex flex-wrap items-end gap-2" role="group" aria-label={stickerItems.length === 1 ? 'Sticker' : 'Stickers'}>
          {stickerItems.map((item) => (
            <div key={item.rowKey} className="relative inline-flex">
              <StickerSlot
                path={item.path}
                label={item.label}
                rowKey={item.rowKey}
                presentationMode={item.presentationMode}
              />
              <AttachmentPathDownloadMenu
                path={item.path}
                fileName={item.label}
                canCopyImage={item.presentationMode === 'webp'}
              />
            </div>
          ))}
        </div>
      )}
      {mediaItems.length > 0 && (
        <div className="flex flex-col gap-2.5">
          {mediaItems.map((item) => (
            <MediaPreviewSlot
              key={item.rowKey}
              path={item.path}
              variant={item.variant}
              fileName={item.fileName}
              rowKey={item.rowKey}
            />
          ))}
        </div>
      )}
      {fileListBody && (
        <div className="min-w-0 max-w-full">
          {fileItems.length > ATTACHMENT_FILE_LIST_SCROLL_THRESHOLD ? (
            <ScrollArea className={cn(fileListOuterClass, 'max-h-44')}>{fileListBody}</ScrollArea>
          ) : (
            <div className={fileListOuterClass}>{fileListBody}</div>
          )}
        </div>
      )}
    </div>
  );
}
